/**
 * Shared readers for the cleaner artist/relation display name off a match row. Used by the SAU-184
 * artist clean-up cell AND by TrackMatchingPage's play-button mapping, so they can't disagree on the
 * value shown vs the value queued.
 *
 * Spotify (and DTO-normalized) rows carry the relation as a nested `{ display_string }` ref; raw
 * RawDdexTrack rows carry an `artists` array of `{ role, name }` credits. Both readers return a
 * trimmed value, or null when absent/blank.
 */

/** Read a trimmed `display_string` off a `{ id, display_string }` nested-model ref, else null. */
export function readNestedDisplay(value: unknown): string | null {
  if (value && typeof value === 'object' && 'display_string' in value) {
    const ds = (value as { display_string?: unknown }).display_string;
    return typeof ds === 'string' && ds.trim() !== '' ? ds.trim() : null;
  }
  return null;
}

/**
 * Read the primary artist name from a DDEX `artists` array of `{ role, name }` credits. Prefers the
 * first `MainArtist` credit — DDEX feeds don't guarantee credit order, so a FeaturedArtist/remixer
 * can appear first — and falls back to the first credit that has a usable name. Returns a trimmed
 * value or null. Because this value is persisted (renaming the shared Artist row), picking the wrong
 * credit would mis-rename every track pointing at it.
 */
export function readArtistsArray(value: unknown): string | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const credits = value
    .filter(
      (credit): credit is { role?: unknown; name?: unknown } =>
        !!credit && typeof credit === 'object' && 'name' in credit
    )
    .map((credit) => ({
      role: typeof credit.role === 'string' ? credit.role : null,
      name: typeof credit.name === 'string' && credit.name.trim() !== '' ? credit.name.trim() : null,
    }))
    .filter((credit): credit is { role: string | null; name: string } => credit.name !== null);

  const mainArtist = credits.find((credit) => credit.role === 'MainArtist');

  return (mainArtist ?? credits[0])?.name ?? null;
}

/** The artist name a click on this match row would write to the live track, or null if none. */
export function readSourceArtistName(row: Record<string, unknown>): string | null {
  return readNestedDisplay(row.artist) ?? readArtistsArray(row.artists);
}
